import debounce from 'lodash.debounce';
import { serialize } from '#/epay3Utilities';

export const api = {
  get,
  post,
  put,
};

export const API_CLIENT_ACTIVITY_EVENT = 'api-client:activity';
const SESSION_TIMEOUT_HEADER = 'X-Session-Timeout-Seconds';

export const baseUrl = globalThis?.epay?.baseUrl;

/** @type {string | null} */
let payableStatementId = null;

/**
 * Set the payableStatementId in order to fetch recent activity
 * @param {string} id
 */
export function setPayableStatementId(id) {
  payableStatementId = id;
}

// This function will be called when the user switches back to the page
function handlePageVisibility() {
  if (document.visibilityState === 'visible') {
    getRecentActivity();
  }
}

document.addEventListener('visibilitychange', handlePageVisibility);

/**
 * Makes a GET request to the specified URL.
 * @template [T=any]
 * @param {string} url - The URL to request.
 * @param {object | null} searchParams - The data to appended to the URL.
 * @param {AbortSignal} [signal]
 * @returns {Promise<T>}
 */
async function get(url, searchParams = null, signal) {
  const requestOptions = {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
    },
  };

  let href = url;

  if (searchParams) {
    const apiUrl = new URL(url, globalThis.location.href);

    Object.entries(searchParams).forEach(([key, value]) => {
      apiUrl.searchParams.append(key, value?.toString() ?? '');
    });

    href = apiUrl.href;
  }

  return handleResponse(href, requestOptions);
}

/**
 * Makes a GET request to the specified URL.
 * @param {string} url - The URL to request.
 * @returns {string}
 */
export function buildUrl(url) {
  if (url.startsWith('.') || url.startsWith('http')) return url;

  if (url.startsWith('/')) {
    return `${baseUrl}${url.substring(1)}`;
  }

  return `${baseUrl}${url}`;
}

/**
 * Returns the base URL of the current page, stripping the last path segment.
 * e.g. https://example.com/Accounts/Edit/123 => https://example.com/Accounts/Edit
 * @returns {string}
 */
export function getPageBaseUrl() {
  const currentUrl = new URL(globalThis.location.href);
  return currentUrl.origin + currentUrl.pathname.split('/').slice(0, -1).join('/');
}

/**
 * Retrieves and processes the recent activity for a given payable statement item.
 * The function is debounced to avoid calling it more than once every 5 minutes.
 * If there is activity then the banners are displayed.
 * If payableStatementId is undefined then it's a noop
 * payableStatementItemId - Should be set in the page by calling {@link setPayableStatementId}

 * @function
 * @async
 * @returns {void} No return value but invokes either `setModifiedMessage` or `setViewedMessage` based on activity.
 */
const getRecentActivity = debounce(
  async function () {
    if (!payableStatementId) return;

    const url = `/PayablesConnect/RecentActivity?id=${payableStatementId}`;
    const recentActivity = await get(url);
    if (recentActivity?.length) {
      globalThis.hideModifiedMessage();
      globalThis.hideViewedMessage();
      for (const activity of recentActivity) {
        if (activity.IsModified) {
          globalThis.setModifiedMessage(activity.Message);
        } else {
          globalThis.setViewedMessage(activity.Message);
        }
      }
    }
  },
  5 * 60 * 1000,
  {
    leading: true, // don't wait to make first call
    trailing: false,
  }
);

/**
 * Makes a POST request to the specified URL with the provided body.
 * @template [T=any]
 * @param {string} url - The URL to request.
 * @param {Object} data - The body of the request.
 * @param {AbortSignal} [signal] - optional - An abort signal.
 * @returns {Promise<T>}
 */
async function post(url, data, signal) {
  // Parsing form data properly in our tests is annoying and we can't post json without removing the anti forgery check
  // eslint-disable-next-line no-undef
  if (process.env.NODE_ENV === 'test') {
    return postJson(url, data);
  }

  const formData = serialize(data, { indices: true });
  const requestOptions = {
    method: 'POST',
    body: formData,
    signal,
  };
  return handleResponse(url, requestOptions);
}

/**
 * Makes a JSON POST request to the specified URL with the provided body.
 * @param {string} url - The URL to request.
 * @param {Object} data - The body of the request.
 * @returns {Promise<any>}
 */
async function postJson(url, data) {
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
  return handleResponse(url, requestOptions);
}

/**
 * Makes a PUT request to the specified URL with the provided body.
 * @param {string} url - The URL to request.
 * @param {Object} body - The body of the request.
 * @returns {Promise<any>}
 */
async function put(url, body) {
  const requestOptions = {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  return handleResponse(url, requestOptions);
}

/**
 * Handles the response from a fetch request.
 * @param {string} url
 * @param {RequestInit | undefined} requestOptions
 * @returns {Promise<any>}
 */
async function handleResponse(url, requestOptions) {
  const fullUrl = buildUrl(url);
  if (!fullUrl.includes('RecentActivity')) {
    getRecentActivity();
  }

  const response = await fetch(fullUrl, requestOptions);
  const timeoutSeconds = syncSessionTimeoutMeta(response);
  if (timeoutSeconds != null) {
    document.dispatchEvent(new CustomEvent(API_CLIENT_ACTIVITY_EVENT, { detail: { timeoutSeconds } }));
  }

  let data = undefined;
  try {
    const text = await response.text();
    data = text && JSON.parse(text);
  } catch (e) {
    console.log('Error parsing server response', e);
  }

  if (!response.ok) {
    const error = data || response.statusText || response.status;

    // because we don't consistently case the "Success" flag
    if (
      typeof error !== 'number' &&
      !Array.isArray(data) &&
      (!Object.hasOwn(error, 'Success') || !Object.hasOwn(error, 'success'))
    ) {
      error.Success = error.success = false;
    }

    if (response.status === 417) {
      if (data?.code === 'REMFA') {
        document.dispatchEvent(new CustomEvent('mfa-verify-modal:create'));
      } else if (data?.code === 'RESSO') {
        globalThis.location.href = data?.redirectUrl;
      }
    }

    return Promise.reject({
      error,
      httpStatusCode: response.status,
      httpStatusText: response.statusText,
    });
  }

  // attempt to augment the data structure with the status codes and stuff
  if (typeof data === 'object' && !Array.isArray(data)) {
    return { ...data, httpStatusCode: response.status, httpStatusText: response.statusText };
  }

  return data;
}

/**
 * Keeps the client timeout metadata in sync with the server-reported session timeout.
 * @param {Response} response
 * @returns {number | null}
 */
function syncSessionTimeoutMeta(response) {
  const headerValue = response.headers.get(SESSION_TIMEOUT_HEADER);
  const timeoutSeconds = Number.parseInt(headerValue ?? '', 10);
  if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) return null;

  const timeoutMeta = document.querySelector('meta[name="session-timeout-seconds"]');
  if (timeoutMeta) {
    timeoutMeta.setAttribute('content', timeoutSeconds.toString());
  }

  return timeoutSeconds;
}
